import { queryParams } from "helpers/reqparams";
import moment from "moment";
import { alertRequest, body, request } from "services/request";

const resource = "api/billing";
const silentSuccessAlertParams = { successAlerts: false, noContentAlerts: true };

type BillingNotificationType = "NewInvoice" | "SubscriptionExpires" | "InvoiceExpired";

type BillingNotificationSettingParams = {
    type: BillingNotificationType;
    flags: number;
};

type BillingNotificationUserParams = {
    userId: number;
};

const getSubscriptions = (params: { from?: string; to?: string } = {}) => {
    const requestParams: { from?: string; to?: string } = {};

    if (params.from) {
        requestParams.from = moment(params.from).format("YYYY-MM-DD");
    }
    if (params.to) {
        requestParams.to = moment(params.to).format("YYYY-MM-DD");
    }
    const paramsString = Object.keys(requestParams).length ? queryParams(requestParams) : "";

    return request(`${resource}/getSubscriptions${paramsString}`, { method: "GET" }).then((data) => {
        if (data) {
            data.subscriptions = data.subscriptions.sort((a: { tariffType: string }) =>
                a.tariffType.startsWith("Logistic") ? -1 : 1,
            );
        }
        return data;
    });
};

const syncOrganization = () => request(`${resource}/syncOrganization`, {});

const createPdfBlob = (data: string) => {
    const imageContent = atob(data);
    const buffer = new ArrayBuffer(imageContent.length);
    const view = new Uint8Array(buffer);
    for (let n = 0; n < imageContent.length; n++) {
        view[n] = imageContent.charCodeAt(n);
    }

    return new Blob([buffer], { type: "application/pdf" });
};

const getDocumentPdf = (documentId: number) =>
    request(`${resource}/getDocumentFile/${documentId}`, { method: "GET" }).then(({ fileName, data }) => {
        const blob = createPdfBlob(data);
        const url = window.URL.createObjectURL(blob);

        return { fileName, blob, url };
    });

const getDocumentFile = (documentId: number) =>
    getDocumentPdf(documentId).then(({ fileName, url }) => {
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    });

const getNotificationUsers = () => request(`${resource}/getNotificationUsers`, { method: "GET" });

const addUserToNotification = (params: BillingNotificationUserParams) =>
    alertRequest(`${resource}/addUserToNotification`, body(params));

const removeUserFromNotification = (params: BillingNotificationUserParams) =>
    alertRequest(`${resource}/removeUserFromNotification`, body(params));

const applyNotificationSetting = (params: BillingNotificationSettingParams) =>
    alertRequest(`${resource}/applyNotificationSetting`, body(params), silentSuccessAlertParams);

export {
    getSubscriptions,
    syncOrganization,
    getDocumentFile,
    getDocumentPdf,
    getNotificationUsers,
    addUserToNotification,
    removeUserFromNotification,
    applyNotificationSetting,
};
